import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { LinkHighlightArea } from "scmp-app/components/content/content-item-render/highlight/styles";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { plusPrimaryContentItemContent$key } from "scmp-app/queries/__generated__/plusPrimaryContentItemContent.graphql";

import {
  Container,
  Content,
  ContentLink,
  Cover,
  CoverLink,
  Footer,
  Header,
  Headline,
  StyledPlusArticleTypeTitle,
  Summary,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  onClick?: () => void;
  reference: plusPrimaryContentItemContent$key;
  withArticleType?: boolean;
  withPublishedDate?: boolean;
  withSummary?: boolean;
};

export const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref: forwardReference,
  reference: reference_,
  withArticleType = true,
  withPublishedDate = true,
  withSummary = true,
}) => {
  const entity = useFragment(
    graphql`
      fragment plusPrimaryContentItemContent on Content {
        __typename
        ...entityLink
        ...plusArticleTypeTitleContent
        ...hooksContentItemProvidersContent
          @arguments(
            withHeadline: true
            withSummary: true
            withActionBar: true
            withCoverImage: true
            withCoverImageSize540x360: true
          )
      }
    `,
    reference_,
  );

  const providers = useContentItemProviders(entity);
  return (
    <Container className={className} ref={forwardReference}>
      <Content>
        <Header>
          {withArticleType && <StyledPlusArticleTypeTitle reference={entity} />}
          <LinkHighlightArea>
            <ContentLink onClick={onClick}>
              <Headline>
                {providers.headline({
                  hideTooltip: true,
                  preferSocialHeadline: true,
                })}
              </Headline>
              {withSummary && (
                <Summary>
                  {providers.summary({
                    isTextOverflowEllipsis: true,
                  })}
                </Summary>
              )}
            </ContentLink>
          </LinkHighlightArea>
        </Header>
        <Footer>
          {withPublishedDate &&
            providers.actionBar({
              displayDateVariant: "publishedDate",
              isBookmarkVisible: false,
              isCommentsVisible: false,
              isIconVisible: false,
            })}
        </Footer>
      </Content>
      <Cover>
        <CoverLink onClick={onClick}>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size540x360",
              mobileUp: "size540x360",
              tabletUp: "size540x360",
            },
            variant: "plus",
          })}
        </CoverLink>
      </Cover>
    </Container>
  );
};

Component.displayName = "ContentItemPlusPrimary";
export const ContentItemPlusPrimary = withHighlightedHeadline(Component);
